import { Loader2 } from 'lucide-react';

export type MisConfirmAction = 'APPROVED' | 'REJECTED' | 'UNAPPROVE' | 'UNREJECT';

const COPY: Record<MisConfirmAction, { title: string; message: string; confirm: string; color: string }> = {
  APPROVED: {
    title: 'Confirm Approve',
    message: 'Approve this idea?',
    confirm: 'Approve',
    color: 'oklch(0.58 0.11 165.46)',
  },
  REJECTED: {
    title: 'Confirm Reject',
    message: 'Reject this idea? Reject is considered a duplicate.',
    confirm: 'Reject',
    color: 'oklch(0.62 0.18 22.45)',
  },
  UNAPPROVE: {
    title: 'Confirm Unapprove',
    message: 'Unapprove this idea?',
    confirm: 'Unapprove',
    color: 'oklch(0.62 0.14 55)',
  },
  UNREJECT: {
    title: 'Confirm Unreject',
    message: 'Unreject this idea?',
    confirm: 'Unreject',
    color: 'oklch(0.62 0.14 55)',
  },
};

export function MisActionConfirm({
  action,
  description,
  onDescriptionChange,
  error,
  pending,
  onCancel,
  onConfirm,
}: {
  action: MisConfirmAction;
  description: string;
  onDescriptionChange: (value: string) => void;
  error: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const copy = COPY[action];
  const needsDescription = action === 'UNAPPROVE' || action === 'UNREJECT';

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/30 p-6"
      onClick={() => {
        if (!pending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-sm font-bold text-slate-800">{copy.title}</h3>
        <p className="text-sm text-slate-600 mt-2">{copy.message}</p>
        {needsDescription && (
          <div className="mt-4">
            <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              placeholder="Reason"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
          </div>
        )}
        {error && <p className="text-red-500 text-xs font-semibold mt-3">{error}</p>}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            style={{ backgroundColor: copy.color }}
            className="relative inline-flex items-center justify-center min-w-[6.75rem] h-8 px-3 rounded-lg text-white text-[10px] font-bold uppercase tracking-wide hover:brightness-95 disabled:cursor-wait"
          >
            <span className={pending ? 'invisible' : ''}>{copy.confirm}</span>
            {pending && <Loader2 className="absolute w-3.5 h-3.5 animate-spin" />}
          </button>
        </div>
      </div>
    </div>
  );
}
